import { SearchableTypeEnum } from 'graphql/types/globalTypes';
import pluralize from 'pluralize';
import { IMAGE_CREATE, FILE_CREATE, AUDIO_CREATE, VIDEO_CREATE, FILE_UPDATE, VIDEO_UPDATE, AUDIO_UPDATE, IMAGE_UPDATE } from "graphql/entry";
import { IMAGE_EXTENSIONS, AUDIO_EXTENSIONS, VIDEO_EXTENSIONS } from "constants/index";
import { getExtension } from 'utils/helpers';
import { FileArgs } from './index';
import { uploadFile } from './uploadFile';

/**
 * Add or create a file record on the Database.
 * @param client Apollo client
 * @param file The file to add or create.
 * @param path The path of the file that was saved or already exists.
 * @param existingId If already existing, the Id of that file record.
 * @param callbackUploadProgress a callback to update upload progress on uploader state.
 */
export async function saveFileRecord({ client, file, path, existingId, callbackUploadProgress, iptc = null }: FileArgs) {
  const extension = getExtension(file.name);
  const extLower = (extension || '').toLowerCase();
  let uploadQuery = existingId ? FILE_UPDATE : FILE_CREATE;
  let type = SearchableTypeEnum.FILE;
  const fileName = file.name;
  let newPath = path;

  if (existingId) {
    newPath = `${path}${fileName}`;
  }

  const variables: any = {
    url: encodeURI(newPath),
  };

  // Determine specific query based on file type.
  // Set type based on file type.
  if (IMAGE_EXTENSIONS.indexOf(extLower) !== -1) {
    uploadQuery = existingId ? IMAGE_UPDATE : IMAGE_CREATE;
    type = SearchableTypeEnum.IMAGE;
  } else if (AUDIO_EXTENSIONS.indexOf(extLower) !== -1) {
    uploadQuery = existingId ? AUDIO_UPDATE : AUDIO_CREATE;
    type = SearchableTypeEnum.AUDIO;
  } else if (VIDEO_EXTENSIONS.indexOf(extLower) !== -1) {
    uploadQuery = existingId ? VIDEO_UPDATE : VIDEO_CREATE;
    type = SearchableTypeEnum.VIDEO;
  }

  let operation = "Create";
  // Add existingId to graphQL variables id.
  if (existingId) {
    variables.ids = [existingId];
    operation = "Update";
  } else {
    variables.remote = false;
    variables.searchable = true;
  }

  // Set image related variable values that get saved to the database.
  if (type === SearchableTypeEnum.IMAGE) {
    let credit, description, width, height, caption;

    if(file.credit && file.credit !== '') { // set credit value if entered at the time of insert
      credit = file.credit;
    } else if (iptc && typeof iptc === "object") {
      Object.keys(iptc).forEach((key) => {
        if (key.indexOf("photoshop:Credit") !== -1 || key.indexOf("iptc:Credit") !== -1 ) {
          credit = iptc[key] ? iptc[key] : credit;
        } else if (key.indexOf("exif:ImageDescription") !== -1) {
          description = iptc[key];
          caption = iptc[key];
        } else if (key.indexOf("width") !== -1) {
          width = iptc[key];
        } else if (key.indexOf("height") !== -1) {
          height = iptc[key];
        }
      })
    }

    variables.width = width ? width : null;
    variables.height = height ? height : null;

    if (!existingId) {
      variables.credit = credit ? credit : null;
      variables.description = description ? description : null;
      variables.caption = caption ? caption : null;
    }

    // Set altText from file.altText if provided, otherwise default to title
    if (file.altText) {
      variables.altText = file.altText;
    } else if (!existingId) {
      variables.altText = variables.title;
    }

    // Override title if explicitly set on the file
    if (file.title) {
      variables.title = file.title;
    }
  }

  try {
    const response = await client.mutate({
      mutation: uploadQuery,
      variables: { input: variables },
    });

    const lowerCaseType = type.toLowerCase();
    const mutationKey = `${lowerCaseType}${operation}`;
    const mutationResult = response.data[mutationKey];

    // Check for success: false in the mutation response
    if (mutationResult && mutationResult.success === false) {
      const errorMessages = mutationResult.errors;
      let userFriendlyMessage = 'Failed to save file record';
      
      if (Array.isArray(errorMessages) && errorMessages.length > 0) {
        // Extract meaningful error message for the user
        const firstError = errorMessages[0];
        if (typeof firstError === 'string') {
          // Handle filename conflict errors - matches formats like:
          // "Url filename conflicts with existing image '/0623/lizard.png' in the same directory"
          // or "filename conflicts with existing '/path/file.png'"
          if (firstError.includes('filename conflicts with existing') || firstError.includes('Url filename conflicts')) {
            // Try to extract filename from various formats
            const filenameMatch = firstError.match(/['"]([^'"]+)['"]/) || firstError.match(/\/([^\/\s]+\.\w+)/);
            const filename = filenameMatch ? filenameMatch[1].split('/').pop() : 'this file';
            userFriendlyMessage = `A file named "${filename}" already exists in this folder. Please rename your file or choose a different folder.`;
          } else {
            // Strip ActiveRecord error prefix for cleaner message
            userFriendlyMessage = firstError.replace(/^ActiveRecord::\w+:\s*/, '');
          }
        }
      }
      
      callbackUploadProgress({ uploading: false, percent: 100, error: true });
      throw new Error(userFriendlyMessage);
    }

    let typeToAccessInRes = !existingId ? lowerCaseType : pluralize(lowerCaseType);
    let id;

    if (typeToAccessInRes === "audio" && existingId) {
      typeToAccessInRes = "audios";
    }

    if (operation === "Update") {
      id = response.data[mutationKey][typeToAccessInRes][0].id;
    } else {
      id = response.data[mutationKey][typeToAccessInRes].id;
    }

    if (existingId) {
      await uploadFile({ client, file, path, existingId, callbackUploadProgress });
    }

    callbackUploadProgress({ uploading: false, percent: 100 });

    return Promise.resolve({
      id,
      type,
      row: response.data[mutationKey][typeToAccessInRes],
    });

  } catch(error: any) {
    callbackUploadProgress({ uploading: false, error: true });
    // Re-throw the error so it can be handled by the caller
    throw error;
  }
}
